import { COPILOT_LAST_OPEN } from 'chatspot-core/constants/LocalStorage';
import { BREEZE_COPILOT, CHAT_AGENT } from 'chatspot-core/types/CopilotAssistantTypes';
import { NAV } from 'chatspot-core/types/LayoutVariants';
import Raven from 'raven-js';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { updateAssistant, updateAssistantType } from '../assistants/slices/assistants';
import { initializeAblyChannel } from '../chat/actions/initializeAblyChannel';
import { selectChatFetchStatus as v1SelectChatFetchStatus } from '../chat/selectors/chatSelectors';
import { selectAblyChannelConnectingState, selectFetchStatus as v2SelectFetchStatus } from '../chat/selectors/chatv2Selectors';
import { selectCopilotThreadIdQueryParam } from '../chat/selectors/parentAppSelectors';
import { THREAD_INITIAL_MESSAGES_COUNT_TO_FETCH_V1, THREAD_INITIAL_MESSAGES_COUNT_TO_FETCH_V2 } from '../constants/threads';
import { useIsUngatedForCopilotV2 } from '../gating/hooks/useIsUngatedForCopilotV2';
import { useHostAppContext } from '../hostAppContext/hooks/useAppContext';
import { getCopilotThreadIdFromSuperstore, setCopilotThreadIdInSuperstore } from '../superstore/utils/copilotThreadId';
import { copilotV2GetLatestOrCreateThread } from '../threads/actions/copilotV2GetLatestOrCreateThread';
import { createThread } from '../threads/actions/createThread';
import { getStartupThread } from '../threads/actions/getStartupThread';
import { getFromLocalStorage } from '../utils/localStorage';
import { useAssistants } from './assistants/internal/hooks/getAssistants';
import { BREEZE_COPILOT_ASSISTANT } from './assistants/internal/constants/assistants';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * ANY CHANGES TO THIS FILE MAY REQUIRE UPDATES TO quickFetchRequest.ts
 */
const ThreadsInit = ({
  children
}) => {
  const dispatch = useDispatch();
  const {
    variant,
    isPreviewMode,
    customCopilotId
  } = useHostAppContext();
  const {
    assistants,
    loading: assistantsLoading
  } = useAssistants();
  const copiloptThreadIdQueryParam = useSelector(selectCopilotThreadIdQueryParam);
  const ablyChannelConnectingState = useSelector(selectAblyChannelConnectingState);
  const isUngatedForCopilotV2 = useIsUngatedForCopilotV2();

  // TODO: Remove this once we drop chatv1. .
  const v1ChatFetchStatus = useSelector(v1SelectChatFetchStatus);
  const v2ChatFetchStatus = useSelector(v2SelectFetchStatus);
  const chatFetchStatus = isUngatedForCopilotV2 ? v2ChatFetchStatus : v1ChatFetchStatus;
  useEffect(() => {
    // dev note: Here we are waiting for the query param to be set to empty string, onLoad once the app is ready we set the query param for nav
    // to a value or empty string and only then proceed here to avoid making redundant api calls (this happens in the NavigationComponentSetup.tsx file)
    if (copiloptThreadIdQueryParam === null && variant === NAV) {
      return;
    }
    const initializeThread = async () => {
      if (chatFetchStatus !== 'idle' || assistantsLoading) {
        return;
      }
      try {
        // In preview mode, always create a new thread to avoid reusing existing threads
        if (isPreviewMode) {
          // Use fallback values for assistant type and id
          const assistantType = customCopilotId ? CHAT_AGENT : BREEZE_COPILOT;
          if (isUngatedForCopilotV2) {
            // Use direct Ably channel initialization for CopilotV2 in preview mode
            // This avoids the switchAblyThread requirement for existing ablyChannelId
            await dispatch(initializeAblyChannel({
              assistantType,
              customCopilotId
            })).then(() => {
              const assistantToSelect = assistants.find(assistant => assistant.id === customCopilotId) || BREEZE_COPILOT_ASSISTANT;
              dispatch(updateAssistant(assistantToSelect));
            });
          } else {
            // Use explicit thread creation for CopilotV1 in preview mode
            await dispatch(createThread({
              assistantType,
              customCopilotId
            }));
          }
          return;
        }

        // Non-preview mode: use existing logic with stored thread IDs
        const superstoreThreadId = await getCopilotThreadIdFromSuperstore();

        // Determine which thread ID to use
        let threadIdToUse = undefined;
        if (copiloptThreadIdQueryParam) {
          // Query param takes precedence
          const parsedQueryParamId = parseInt(copiloptThreadIdQueryParam, 10);
          threadIdToUse = parsedQueryParamId;
          // Update superstore with query param ID
          await setCopilotThreadIdInSuperstore(parsedQueryParamId);
        } else if (variant === NAV && superstoreThreadId) {
          // Use stored thread ID for NAV variant
          threadIdToUse = superstoreThreadId;
        }
        if (isUngatedForCopilotV2) {
          // TODO- @mlegocki: refactor this logic. We shouldn't need this extra status for bootup
          // This works for now because every time we disconnect from a channel, we set the status to 'idle' and we don't want this to run whenever 'idle' is set
          if (ablyChannelConnectingState === 'initial-bootup-initialized') {
            var _parseInt;
            // We need to kick-off the thread initialization because our app expects the chat already be loaded or we're stuck in a blank state (see RequiredAppSetup.tsx)
            // TODO - @mlegocki: handle passing `customCopilotId`. May relate to effort for CopilotV2 supporting Sales Copilot: https://git.hubteam.com/HubSpot/copilot-issues/issues/107
            await dispatch(copilotV2GetLatestOrCreateThread({
              latestUserActivityAt: (_parseInt = parseInt(getFromLocalStorage(COPILOT_LAST_OPEN), 10)) !== null && _parseInt !== void 0 ? _parseInt : Date.now(),
              messageLimit: THREAD_INITIAL_MESSAGES_COUNT_TO_FETCH_V2,
              threadId: threadIdToUse,
              isTemporary: false
            })).then(response => {
              var _response$payload;
              // This logic is added to switch to the correct custom id on load itself
              const currentThreadCopilotToSwitchTo = (_response$payload = response.payload) !== null && _response$payload !== void 0 && (_response$payload = _response$payload.thread) !== null && _response$payload !== void 0 && _response$payload.customCopilotId ? assistants.find(assistant => {
                const customCopilotIdFromThread = response.payload.thread.customCopilotId;
                return assistant.id === parseInt(customCopilotIdFromThread, 10);
              }) : undefined;
              if (currentThreadCopilotToSwitchTo) {
                dispatch(updateAssistant({
                  type: currentThreadCopilotToSwitchTo.type,
                  name: currentThreadCopilotToSwitchTo.name,
                  id: currentThreadCopilotToSwitchTo.id,
                  welcomeMessage: (currentThreadCopilotToSwitchTo === null || currentThreadCopilotToSwitchTo === void 0 ? void 0 : currentThreadCopilotToSwitchTo.welcomeMessage) || ''
                }));
              }
            });
          }
        } else {
          var _threadIdToUse, _getFromLocalStorage;
          await dispatch(getStartupThread({
            threadId: ((_threadIdToUse = threadIdToUse) === null || _threadIdToUse === void 0 ? void 0 : _threadIdToUse.toString()) || '',
            timeZoneOffset: new Date().getTimezoneOffset(),
            latestActivityTimestamp: (_getFromLocalStorage = getFromLocalStorage(COPILOT_LAST_OPEN)) !== null && _getFromLocalStorage !== void 0 ? _getFromLocalStorage : Date.now(),
            messagesLimit: THREAD_INITIAL_MESSAGES_COUNT_TO_FETCH_V1
          })).then(response => {
            var _payload;
            // Update the assistant type to the assistant type from the response
            const assistantType = response === null || response === void 0 || (_payload = response.payload) === null || _payload === void 0 ? void 0 : _payload.assistantType;
            dispatch(updateAssistantType(assistantType));
          });
        }
      } catch (error) {
        Raven.captureException(error instanceof Error ? error : new Error(String(error)));
      }
    };
    void initializeThread();
  }, [ablyChannelConnectingState, chatFetchStatus, copiloptThreadIdQueryParam, dispatch, variant, isUngatedForCopilotV2, assistants, assistantsLoading, isPreviewMode, customCopilotId]);

  // When there is no thread, we don't want to activate the widget at all
  if (chatFetchStatus === 'initializing' || chatFetchStatus === 'idle') {
    return null;
  }
  if (chatFetchStatus === 'failed') {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "CHAT_FETCH_FAILURE"
    });
  }
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "THREAD_SETUP_SUCCESS",
    children: children
  });
};
export default ThreadsInit;